import { FatTransactionsPageAllocationTransactionsQuery } from '@/__generated__/graphql';
import client from '@/apollo-client';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { DeleteBucketIcon } from '@/assets/static/icons/delete-bucket';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select from '@/components/basicComponents/select';
import Textarea from '@/components/basicComponents/textarea';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { terminateInputSchema } from '@trace-8/trace8-schema';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { z } from 'zod/v4';
import { ALLOCATION_QUERY, EXECUTE_ACTION_MUTATION } from '../queries';
import { IExecuteActionMutationInput, IExecuteActionMutationResponse } from '../types';

const items = [
  { label: 'Account Closure', value: 'Account Closure' },
  { label: 'Client Preference', value: 'Client Preference' },
  { label: 'Client Unresponsive', value: 'Client Unresponsive' },
  { label: 'Manager Rejection', value: 'Manager Rejection' },
  { label: 'Other', value: 'Other' }
];

type TerminateInput = z.infer<typeof terminateInputSchema>;

const defaultFormValues = {
  terminationReason: '',
  terminationNote: ''
};
interface TerminateProps {
  isOpen: boolean;
  id: string;
  onClose: () => void;
  updateLocalAllocation: (updatedAllocation: FatTransactionsPageAllocationTransactionsQuery['allocationTransactions']['allocations'][number]) => void;
  startFlashRowAnimation: (allocationId: string) => void;
  operationWorkflowTransitionTaskSummariesRefetch: () => void;
}

const Terminate = ({
  isOpen,
  id,
  onClose,
  updateLocalAllocation,
  startFlashRowAnimation,
  operationWorkflowTransitionTaskSummariesRefetch
}: TerminateProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const { control, handleSubmit } = useForm<{ terminationReason: string; terminationNote: string }>({
    defaultValues: defaultFormValues
  });

  const [executeAction] = useMutation<IExecuteActionMutationResponse<TerminateInput>, IExecuteActionMutationInput<TerminateInput>>(
    EXECUTE_ACTION_MUTATION
  );

  const onTerminate = async (data: { terminationReason: string; terminationNote: string }) => {
    try {
      setShowWarningDialog(false);
      setErrorMessage(null);
      setLoading(true);
      if (!id) {
        setErrorMessage(defaultErrorMessage);
        return;
      }

      await executeAction({
        variables: {
          data: {
            allocationId: id,
            action: 'terminate',
            actionDataPayload: {
              ...data
            }
          }
        }
      });

      operationWorkflowTransitionTaskSummariesRefetch();
      startFlashRowAnimation(id);
      onClose();
      updateAllocationRowData();
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updateAllocationRowData = async () => {
    try {
      const { data: allocationData } = await client.query({
        query: ALLOCATION_QUERY,
        variables: {
          id
        }
      });

      updateLocalAllocation(allocationData.Allocation);
    } catch (error) {
      console.error(error);
    }
  };

  if (showWarningDialog) {
    return (
      <ModalWrappedContent
        isOpen={isOpen}
        onClose={onClose}
        confirmButton={{
          name: 'Confirm',
          onClick: handleSubmit(onTerminate)
        }}
      >
        <ContentWrapper>
          <DeleteBucketIcon fill={themeVar('font.disabled')} />
          <ModalTitle>Terminate Transaction</ModalTitle>
          <ModalDescription>
            This action cannot be undone. If appropriate, you can start over with a new subscription for this investor. Are you absolutely sure that
            you want to proceed?
          </ModalDescription>
        </ContentWrapper>
      </ModalWrappedContent>
    );
  }

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Terminate Transaction"
      showCloseIcon={!loading}
      showCancelButton={!loading}
      isUseOutsideClick={!loading}
      confirmButton={{ name: 'Submit', onClick: handleSubmit(() => setShowWarningDialog(true)), disabled: loading }}
    >
      {loading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <ErrorBannerWrapper>
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            </ErrorBannerWrapper>
          )}
          <ModalDescription>
            This action cannot be undone. If appropriate, you can start over with a new subscription for this investor.
          </ModalDescription>
          <ContentWrapper>
            <Controller
              name="terminationReason"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Reason is required'
                }
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Select
                  label="Reason"
                  value={value}
                  setValue={onChange}
                  options={[{ label: 'Select', value: '' }, ...items]}
                  defaultSelectLabel="Select"
                  size="md"
                  errorText={error?.message}
                  isRequiredField
                />
              )}
            />

            <Controller
              name="terminationNote"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Textarea label="Comments" value={value} onChange={onChange} placeholder="Enter Reason" size="md" errorText={error?.message} />
              )}
            />
          </ContentWrapper>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default Terminate;

const ModalTitle = styled.div`
  font-size: 33px;
  font-weight: 400;
  line-height: 46.2px;
  text-align: center;
  color: ${themeVar('font.strong')};
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;

const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
`;

const ModalDescription = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  color: ${themeVar('font.base')};
`;
